// Drawer layout for the "Captured items on this wheel" surface (ISP-4068 /
// WL-021b). LAYOUT primitives only (backdrop + slide-in rail + header/body) —
// composed from the design tokens (single source of truth). The card/list
// surfaces themselves are the SHARED capture styleds imported from `../styles`
// (Card, Thumb, CardBody, CardMeta, TypeChip, List, StateWrap, EmptyArt,
// SkeletonCard, SkeletonBox), never hand-rolled here (G-21).
//
// Right-rail drawer matches the app's existing wheel-side panel pattern
// (NewHotbar / BusinessBoxBuilder are right-rail panels on ViewWheel) and the
// deck's "Capture drawer off the right rail" concept (capture-elevated 03).

import styled, { css } from "styled-components";
import { colors } from "../../../utils";
import { spacing, radius, shadow, motion, zIndex } from "../../../theme/tokens";
import {
  CAPTURE_DRAWER_FULL_BLEED_MAX_PX,
  CAPTURE_DRAWER_WIDTH_PX,
} from "./captureDrawerMetrics";

const px = (n) => `${n}px`;

/**
 * The scrim — and the ONE viewport range it is allowed to exist in.
 *
 * WHY THIS MEDIA QUERY IS THE WHOLE FIX (measured live on web-stg, bundle
 * main.529b92bf.js, 2026-08-05, headed Chromium at 1440x960):
 *
 *   drawer OPEN   real click on [data-cy="autosave-chip"] TIMED OUT at 8013ms
 *                 and 8016ms (two independent sessions, overlays dismissed);
 *                 document.elementFromPoint() at the chip's centre returned
 *                 this element - fixed, z-index 1300, 1440x960,
 *                 rgba(18,48,81,0.28), pointer-events auto.
 *   drawer CLOSED same click succeeded in 45ms  (the probe works)
 *   scrim neutralised (pointer-events:none), drawer still OPEN:
 *                 same click succeeded in 43ms  (the scrim was the sole blocker)
 *
 * Above the full-bleed breakpoint the drawer does NOT cover the page: the
 * layout root shrinks by exactly this drawer's width (see captureDrawerMetrics
 * + WheelLayout's MainDiv) so the builder stays fully visible BESIDE it. The
 * user is expected to keep working - saving, editing, using the rail - while
 * the drawer is open. A scrim over that content contradicts the push and, as
 * measured, makes every one of those controls unreachable. So in push mode the
 * scrim is not dimmed or softened, it is NOT RENDERED: `display: none` removes
 * it from paint AND from hit-testing, which `opacity: 0` alone would not.
 *
 * At or below the breakpoint the drawer goes full-bleed - there is nothing left
 * beside it to interact with - so the modal scrim (and its click-to-close) is
 * correct and stays exactly as it was.
 */
export const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(18, 48, 81, 0.28);
  z-index: ${zIndex.overlay};
  opacity: ${(p) => (p.$open ? 1 : 0)};
  visibility: ${(p) => (p.$open ? "visible" : "hidden")};
  transition: opacity ${motion.duration.base} ${motion.easing.standard},
    visibility ${motion.duration.base} ${motion.easing.standard};

  @media (min-width: ${px(CAPTURE_DRAWER_FULL_BLEED_MAX_PX + 1)}) {
    display: none;
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

export const Drawer = styled.aside`
  position: fixed;
  top: 0;
  right: 0;
  height: 100vh;
  /* Single source of truth — the builder layout reads the SAME constant to
     decide how far to push its chrome inward (see captureDrawerMetrics). */
  width: ${px(CAPTURE_DRAWER_WIDTH_PX)};
  max-width: 100vw;
  background: ${colors.white};
  box-shadow: ${shadow.e3};
  z-index: ${(p) => zIndex.overlay + 1};
  display: flex;
  flex-direction: column;
  transform: translateX(${(p) => (p.$open ? "0" : "100%")});
  transition: transform ${motion.duration.slow} ${motion.easing.standard};
  box-sizing: border-box;
  overflow: hidden;

  @media (max-width: ${px(CAPTURE_DRAWER_FULL_BLEED_MAX_PX)}) {
    width: 100%;
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }

  /* ISP-9999 — embedded mode: rendered INSIDE the builder's own side-pane slot
     (SegmentBuilder/index.jsx sidePaneList), which already owns position,
     width and the open/close animation via the same react-spring width the
     other 7 rail panes use — matching them exactly, on the LEFT of the rail,
     instead of this drawer's own fixed-to-the-true-viewport-edge box (which
     put it on the far right, past the rail, and needed WheelLayout's now-
     removed push hack just to avoid covering the header). A plain in-flow
     block filling that slot is all that's needed here; last declarations in
     this rule win, so this cleanly overrides the standalone-drawer box above
     without a second styled component. */
  ${({ $embedded }) =>
    $embedded &&
    css`
      position: static;
      top: auto;
      right: auto;
      /* Match the builder side-pane column height so the list scrolls inside
         DrawerBody instead of growing the pane (2573px+ with no scrollbar). */
      height: calc(92vh - var(--iyuga-consent-reserve, 0px));
      max-height: calc(92vh - var(--iyuga-consent-reserve, 0px));
      min-height: 0;
      width: 100%;
      max-width: 100%;
      box-shadow: none;
      transform: none;
      transition: none;

      @media (max-width: ${px(CAPTURE_DRAWER_FULL_BLEED_MAX_PX)}) {
        width: 100%;
        height: calc(100vh - var(--iyuga-consent-reserve, 0px));
        max-height: calc(100vh - var(--iyuga-consent-reserve, 0px));
      }
    `}
`;

export const DrawerHeader = styled.header`
  display: flex;
  align-items: center;
  gap: ${px(spacing.md)};
  padding: ${px(spacing.lg)} ${px(spacing.xl)};
  border-bottom: 1px solid ${colors.lightBorder};
  flex-shrink: 0;
`;

export const HeaderText = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

export const DrawerTitle = styled.h2`
  margin: 0;
  font-size: 16px;
  font-weight: 700;
  color: ${colors.black};
  line-height: 1.3;
`;

export const DrawerSubtitle = styled.span`
  font-size: 12px;
  font-weight: 500;
  color: ${colors.textGrey};
`;

export const DrawerBody = styled.div`
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  scrollbar-gutter: stable;
  padding: ${px(spacing.lg)} ${px(spacing.xl)} ${px(spacing.xl)};
`;

// A single read-only capture row is composed from the SHARED capture card
// styleds; this is just the retry button for the error state (a control, not a
// card surface).
export const RetryButton = styled.button`
  border: 1px solid ${colors.PrimaryPurple500};
  background: ${colors.white};
  color: ${colors.PrimaryPurple700};
  border-radius: ${px(radius.sm)};
  padding: ${px(spacing.sm)} ${px(spacing.lg)};
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: background-color ${motion.duration.fast} ${motion.easing.standard};

  &:hover {
    background: ${colors.Highlight};
  }

  &:focus-visible {
    outline: none;
    box-shadow: ${shadow.focus};
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }
`;

// A "Load more" affordance for paginated attached lists (kept minimal; the
// drawer body also supports plain scroll).
export const LoadMoreButton = styled(RetryButton)`
  width: 100%;
  margin-top: ${px(spacing.md)};
`;

/**
 * CAPTURE_SEGMENT_DROP — "Add to Segment", the explicit control Kelly asked for
 * "right underneath the captured item".
 *
 * It is NOT styled as a quiet tertiary link. It is the ONLY route to a Segment
 * that a keyboard or screen-reader user has, and the primary route on touch, so
 * it reads as a real action: 32px minimum height (the app's touch-target
 * ruling), the app's standard purple focus ring, and a line-art icon — never an
 * emoji, which is the user's voice, not the product's.
 */
export const AddToSegmentButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  align-self: flex-start;
  min-height: 32px;
  margin-top: ${px(spacing.xs)};
  padding: 6px 12px;
  border: 1px solid #9b5af8;
  border-radius: 8px;
  background: #fbfaff;
  color: #6f27d6;
  font: 600 12px "DM Sans", system-ui, sans-serif;
  cursor: pointer;
  transition: background 120ms ease;

  &:hover:not(:disabled) {
    background: #efe9fe;
  }
  &:focus-visible {
    outline: none;
    box-shadow: ${shadow.focus};
  }
  &:disabled {
    cursor: not-allowed;
    border-color: #d8d4e4;
    background: #f4f2f9;
    color: #7b7690;
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

/**
 * The reason a capture cannot go into a Segment, rendered ON the card.
 *
 * Kelly's recorded attempt produced complete silence — that silence is what
 * caused the same report three times. A refusal must be visible where the user
 * is looking, not only in a toast that may already have gone.
 */
export const CardNotice = styled.p`
  margin: ${px(spacing.xs)} 0 0;
  font: 400 11px "DM Sans", system-ui, sans-serif;
  color: #8b1f1f;
`;

/**
 * QA-77 — the pane's control strip: the two section tabs and, when the visible
 * list holds more than one media type, the media-type filter row.
 *
 * It sits BETWEEN the header and the scrolling body deliberately. Inside
 * DrawerBody the controls would scroll away exactly when a long list makes
 * them most useful; above the header they would push the title out of a 380px
 * rail. `flex-shrink: 0` keeps them from being squeezed by a tall list.
 */
export const DrawerControls = styled.div`
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  gap: ${px(spacing.sm)};
  padding: ${px(spacing.sm)} ${px(spacing.md)};
  border-bottom: 1px solid ${colors.lightBorder};
  background: ${colors.white};
`;
